// FLAGS: personal-data, identity, silent-failure
// Expert (staff) records, their consult lists and the management actions taken on them — reassign, promote, reset 2FA.
import {
  QueryObserverSuccessResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiPaths, pageSize } from "@src/constants";
import { Psychologists } from "@src/api/v1/Psychologists";
import { PsychologistManagement } from "@src/api/v1/PsychologistManagement";
import {
  GetAllSessionsParams,
  GetPsychologistConsultsParams,
  OpenUpEmployeesParams,
  RequestExpertsEditExpertDetailsRequest,
  RequestFilterPsychologistsRequest,
  ResponsePsychologistGetPsychologistConsultsResponse,
  ResponsePsychologistResponsePaginated,
  SetPsychologistThemesPayload,
} from "@src/api/v1/data-contracts";
import { Consults } from "@src/api/v1/Consults";
import { ExpertDetails } from "@src/api/v1/ExpertDetails";
import { Scheduler } from "@src/api/v1/Scheduler";
import { DateFilter } from "@src/models/DateFilter";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { useAuthentication } from "@src/components/wrappers/AuthenticationProvider";
import { fiveMinutes, oneHour } from "./staletimes";
import { queryIdentifiers } from "../utils/constants";

export const useReassignPsychologistMutation = () => {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [queryIdentifiers.consults],
    });
    queryClient.invalidateQueries({
      queryKey: [queryIdentifiers.psychologistManagement],
    });
  };

  return useMutation({
    mutationFn: ({
      userId,
      consultId,
    }: {
      userId: string;
      consultId: number;
    }) =>
      new PsychologistManagement(OpenUpHttpClient).reassignPsychologist(
        userId,
        consultId,
      ),
    onSuccess,
  });
};

export const usePsychologistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      new PsychologistManagement(OpenUpHttpClient).makeClientAPsychologist(
        userId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

export const useOpenUpEmployeeQuery = (
  queryParams: OpenUpEmployeesParams = {},
) =>
  useQuery({
    queryKey: [queryIdentifiers.employee, queryParams],
    queryFn: () =>
      new PsychologistManagement(OpenUpHttpClient).openUpEmployees(queryParams),
  });

export const usePsychologistsQuery = (
  queryParams: { page: number; search?: string; limit: number } = {
    page: 0,
    search: "",
    limit: 10,
  },
  tagsAndThemes: RequestFilterPsychologistsRequest = { tags: [], themes: [] },
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: [queryIdentifiers.psychologists, queryParams, tagsAndThemes],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getPsychologists(
        {
          page: queryParams.page,
          search: queryParams.search,
          limit: queryParams.limit,
        },
        tagsAndThemes,
      ),
    staleTime: fiveMinutes,
    suspense: false,
    enabled,
  }) as QueryObserverSuccessResult<ResponsePsychologistResponsePaginated>;

export const usePsychologistQuery = (psychologistId: string) =>
  useQuery({
    queryKey: [queryIdentifiers.psychologists, psychologistId],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getPsychologistById(psychologistId),
    staleTime: fiveMinutes,
  });

export const usePsychologistThemesQuery = (psychologistId: string) => {
  const queryClient = useQueryClient();

  const { data: psychologistThemes } = useQuery({
    queryKey: [
      queryIdentifiers.psychologists,
      "psychologistThemes",
      psychologistId,
    ],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getPsychologistThemes(psychologistId),
  });

  const { mutateAsync: savePsychologistThemes } = useMutation({
    mutationFn: (themeKeys: SetPsychologistThemesPayload) =>
      new PsychologistManagement(OpenUpHttpClient).setPsychologistThemes(
        psychologistId,
        themeKeys,
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          queryIdentifiers.psychologistManagement,
          "psychologistThemes",
          psychologistId,
        ],
      });
    },
  });

  return {
    psychologistThemes,
    savePsychologistThemes,
  };
};

export const usePsychologistsConsultQuery = (consultId: number) => {
  const { isLoggedIn } = useAuthentication();

  return useQuery({
    queryKey: [
      queryIdentifiers.psychologists,
      queryIdentifiers.consults,
      consultId,
    ],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getClientConsult(consultId),
    enabled: isLoggedIn,
    staleTime: oneHour,
  });
};

// FLAGS: silent-failure
// Primes the per-consult cache from the render body with the *list* row shape, under the same key
// `usePsychologistsConsultQuery` fills from `getClientConsult`. Whichever ran last decides what a consumer typed for
// the other one receives, and nothing reports the mismatch.
export const useExpertsConsultsQuery = (
  queryParams: GetPsychologistConsultsParams = {},
) => {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAuthentication();

  const result = useQuery({
    queryKey: [queryIdentifiers.psychologists, queryParams],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getPsychologistConsults(queryParams),
    enabled: isLoggedIn,
    staleTime: fiveMinutes,
  });

  if (result.isSuccess) {
    result.data.data?.forEach((consult) =>
      queryClient.setQueryData<ResponsePsychologistGetPsychologistConsultsResponse>(
        [queryIdentifiers.psychologists, queryIdentifiers.consults, consult.id],
        consult,
      ),
    );
  }
  return result;
};

export const useAllSessionsQuery = (queryParams: GetAllSessionsParams = {}) => {
  const { isLoggedIn } = useAuthentication();

  return useQuery({
    queryKey: [queryIdentifiers.consults, queryParams],
    queryFn: () => new Consults(OpenUpHttpClient).getAllSessions(queryParams),
    enabled: isLoggedIn,
    staleTime: fiveMinutes,
  });
};

export const useLoggedPsychologist = () =>
  useQuery({
    queryKey: [queryIdentifiers.psychologists, queryIdentifiers.profile],
    queryFn: () => new Psychologists(OpenUpHttpClient).getLoggedPsychologist(),
    staleTime: oneHour,
  });

export const useExpertDetailsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: { userId: string } & Partial<RequestExpertsEditExpertDetailsRequest>) =>
      new ExpertDetails(OpenUpHttpClient).updateExpertDetails(
        userId,
        data as RequestExpertsEditExpertDetailsRequest,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

export const resyncCalendar = (userId: string) =>
  new Scheduler(OpenUpHttpClient).resynchronizeCalendar(userId);

// FLAGS: identity
// Resets an expert's second factor, letting them re-enrol a new authenticator; the API policy is the only gate.
export const useResetTwoFactorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      new PsychologistManagement(OpenUpHttpClient).resetTwoFactorAuthentication(
        userId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

export const useExpertFeedbackQuery = (
  dateFilter: string = DateFilter.AllTime,
  page: number = 0,
  limit: number = pageSize,
  search: string = "",
) =>
  useQuery({
    queryKey: [apiPaths.myFeedbacks, page, limit, search, dateFilter],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getExpertFeedbacks({
        page,
        limit,
        search,
        dateFilter,
      }),
    staleTime: fiveMinutes,
  });
